import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { useAppSelector } from 'hooks/reduxHooks'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import useCurrentLoyaltyPendingPoints from 'loyalty/hooks/useCurrentLoyaltyPendingPoints'
import getLoyaltyTierThresholds from 'loyalty/selectors/getLoyaltyTierThresholds'
import TierProgressionAccordionItem from './TierProgressionAccordionItem'
import getTierBenefitsForProgression from 'loyalty/lib/getTierBenefitsForProgression'
import useCurrentMembershipYear from 'loyalty/hooks/useCurrentMembershipYear'

interface Props {
  tiers: Array<App.LoyaltyTier>
  defaultExpandedTier?: App.LoyaltyTier
  checkoutDate?: string
}

function TierProgressionInfoModalContent({ tiers, defaultExpandedTier, checkoutDate }: Props) {
  const intl = useIntl()
  const tierThresholds = useAppSelector(getLoyaltyTierThresholds)
  const loyaltyAccount = useAppSelector(state => state.loyalty.account.data)
  const [loyaltyConfig] = useLoyaltyConfig()
  const [pendingPoints] = useCurrentLoyaltyPendingPoints()
  const membershipYearEndDate = useCurrentMembershipYear()?.end.toISOString()

  const isSingleTier = tiers.length === 1
  const [expandedTier, setExpandedTier] = useState<App.LoyaltyTier | null>(defaultExpandedTier ?? tiers[tiers.length - 1] ?? null)

  const toggleTier = useCallback((tier: App.LoyaltyTier) => {
    setExpandedTier(prev => prev === tier ? null : tier)
  }, [])

  const pendingCreditsTooltip = intl.formatMessage({
    id: 'Boqoor9d0GY6whbRZZHh',
    defaultMessage: 'Pending status credits may take up to 24hrs to appear',
  })

  const pendingNightsTooltip = intl.formatMessage({
    id: 'Lc4cqOqiGPfrr0XCcytw',
    defaultMessage: 'Pending nights may take up to 24hrs to appear',
  })

  const showLessText = intl.formatMessage({
    id: 'm42k3kxkQ89vFkgg8pud',
    defaultMessage: '- less',
  })

  const showMoreText = intl.formatMessage({
    id: 'Ol4tTKIoO42EEw3WM-WM',
    defaultMessage: '+ more',
  })

  if (!tierThresholds) return null

  const currentStatusCredits = loyaltyAccount?.statusCredits ?? 0
  const currentNights = loyaltyAccount?.numberOfNights ?? 0

  return (
    <VerticalSpacer gap={16}>
      {tiers.map(tier => {
        const isExpanded = isSingleTier || expandedTier === tier
        const tierBenefits = getTierBenefitsForProgression(loyaltyConfig, tier)

        return (
          <TierProgressionAccordionItem
            key={tier}
            tier={tier}
            tierThresholds={tierThresholds}
            currentStatusCredits={currentStatusCredits}
            currentNights={currentNights}
            membershipYearEndDate={membershipYearEndDate}
            pendingPoints={pendingPoints}
            tierBenefits={tierBenefits}
            isExpanded={isExpanded}
            isSingleTier={isSingleTier}
            onToggle={toggleTier}
            pendingCreditsTooltip={pendingCreditsTooltip}
            pendingNightsTooltip={pendingNightsTooltip}
            showLessText={showLessText}
            showMoreText={showMoreText}
            checkoutDate={checkoutDate}
          />
        )
      })}
    </VerticalSpacer>
  )
}

export default TierProgressionInfoModalContent
